import axios from 'axios';
import type { PosOfflineDirectory } from '@mlm/shared';
import { api, type Branch, type PosProduct } from '../api';
import { useOfflineStore } from './state';
import { clearPack, savePack } from './store';

/** A pack older than this is refreshed the next time the till is online. */
export const PACK_FRESH_MS = 15 * 60 * 1000;

/** Requests that must reach the real server, whatever mode the till is in. */
const direct = { networkOnly: true } as Record<string, unknown>;

/**
 * Download what the till needs to run with no internet. Everything or nothing:
 * a half-saved pack could price a member from one moment and a product from
 * another. A failed refresh keeps the pack that is already here.
 */
export async function refreshPack(branchId: string): Promise<'ok' | 'disabled' | 'failed'> {
  try {
    const [branches, products, directory, config, coursePolicy] = await Promise.all([
      api.get<Branch[]>('/branches', direct),
      api.get<PosProduct[]>('/pos/products', { ...direct, params: { branchId } }),
      api.get<PosOfflineDirectory>('/pos/offline/directory', { ...direct, params: { branchId } }),
      api.get('/tenant/config', direct),
      api.get('/pos/course-policy', direct),
    ]);
    await savePack({
      branchId,
      downloadedAt: new Date().toISOString(),
      branches: branches.data,
      products: products.data,
      directory: directory.data,
      config: config.data,
      coursePolicy: coursePolicy.data,
    });
    return 'ok';
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data?.error === 'pos_offline_disabled') {
      // Switched off. Sales still on this device keep it until they sync.
      if (useOfflineStore.getState().pending === 0) await clearPack();
      return 'disabled';
    }
    return 'failed';
  }
}
